import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1496347326319-2935d381b307?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBnbG9iYWwlMjBzaGlwcGluZyUyMGNhcmdvJTIwc2hpcCUyMHNoaXAlMjBzdW5zZXR8ZW58MHx8fHwxNzgyMDc5NDU3fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(user.role === "admin" ? "/admin" : "/portal");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inp = "w-full bg-white border border-zinc-300 rounded-sm px-4 py-3 text-sm focus:border-coral focus:ring-1 focus:ring-coral outline-none transition";

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-sm">
          <Logo />
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-coral mt-8 transition" data-testid="back-home">
            <ArrowLeft size={15} /> Back to site
          </Link>
          <h1 className="font-serif text-4xl font-bold mt-6 tracking-tight">Welcome back.</h1>
          <p className="text-zinc-500 mt-2">Sign in to manage your sourcing requests.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded-sm" data-testid="login-error">{error}</div>}
            <div>
              <label className="text-sm font-semibold">Email</label>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inp} mt-1.5`} data-testid="login-email" />
            </div>
            <div>
              <label className="text-sm font-semibold">Password</label>
              <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={`${inp} mt-1.5`} data-testid="login-password" />
            </div>
            <button disabled={loading} className="w-full bg-coral hover:bg-coral-hover text-white font-semibold py-3 rounded-sm transition disabled:opacity-60 flex items-center justify-center gap-2" data-testid="login-submit">
              {loading ? "Signing in..." : "Sign in"} <ArrowRight size={17} />
            </button>
          </form>
          <p className="text-sm text-zinc-500 mt-6">
            No account? <Link to="/register" className="text-coral font-semibold hover:underline" data-testid="goto-register">Create one</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src={HERO} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <blockquote className="font-serif text-3xl font-medium leading-snug text-balance">"A single, accountable partner for global sourcing."</blockquote>
        </div>
      </div>
    </div>
  );
}
